import {
  FEEDBACK_BODY_TYPE,
  FEEDBACK_TYPE,
  SOURCE_TYPE,
} from '@/types/feedbackConst';

export enum FEEDBACK_FIELD {
  MANAGER = 'manager',
  CUSTOMER_INFO = 'customer_info',
  BOOKMARKS = 'bookmarks',
  DATA_ORIGIN = 'data_origin',
  LABELS = 'labels',
  TOPIC2_LIST = 'topic2_list',
  SENTIMENT = 'sentiment',
  CLASSIFICATION = 'classification',
  DATE = 'date',
  RATING_SCORE = 'rating_score',
  LANGUAGE = 'language',
  REMARKS = 'remarks',
  SOURCE = 'source',
  PRODUCTS = 'products',
  AD_PREDICTION = 'ad_prediction',
  ENDING_SENTIMENT = 'ending_sentiment',
  DEMOGRAPHIC = 'demographic',
  SURVEY = 'survey',
  ENGAGEMENT = 'engagement',
  HASHTAGS = 'hashtags',
  SCORES = 'scores',
  DIRECTION = 'direction',
  STATUS = 'status',
  SOURCE_STATUS = 'source_status',
  PRIORITY = 'priority',
  KEY_SENTIMENT = 'key_sentiment',
  SOURCE_ID = 'source_id',
  MENTIONS = 'mentions',
  IMPRESSION = 'impression',
  ENGAGEMENT_RATE = 'engagement_rate',
  TOTAL_ENGAGEMENT = 'total_engagement',
}

export const getFeedbackType = (
  source: SOURCE_TYPE,
  bodyType: FEEDBACK_BODY_TYPE,
): FEEDBACK_TYPE | null => {
  switch (source) {
    case SOURCE_TYPE.MANUAL:
    case SOURCE_TYPE.CSV:
    case SOURCE_TYPE.ATLAS:
    case SOURCE_TYPE.CHANNELTALK:
    case SOURCE_TYPE.FRESHDESK:
    case SOURCE_TYPE.FRONT:
    case SOURCE_TYPE.HELPSCOUT:
    case SOURCE_TYPE.INTERCOM:
    case SOURCE_TYPE.KUSTOMER:
    case SOURCE_TYPE.SALESFORCE:
    case SOURCE_TYPE.ZENDESK:
    case SOURCE_TYPE.SLACK:
    case SOURCE_TYPE.GONG:
    case SOURCE_TYPE.RICH_PANEL:
      return FEEDBACK_TYPE.TICKET;

    case SOURCE_TYPE.GORGIAS: {
      switch (bodyType) {
        case FEEDBACK_BODY_TYPE.SURVEY:
          return FEEDBACK_TYPE.SURVEY;
        default:
          return FEEDBACK_TYPE.TICKET;
      }
    }

    case SOURCE_TYPE.GOOGLE_PLAY:
    case SOURCE_TYPE.APPLE_APP_STORE:
    case SOURCE_TYPE.G2:
    case SOURCE_TYPE.STAMPED:
    case SOURCE_TYPE.YOTPO:
    case SOURCE_TYPE.TRUSTPILOT:
    case SOURCE_TYPE.OKENDO:
    case SOURCE_TYPE.OLIVEYOUNG:
    case SOURCE_TYPE.AMAZON:
    case SOURCE_TYPE.QOO10:
    case SOURCE_TYPE.COUPANG:
    case SOURCE_TYPE.NAVER_STORE:
    case SOURCE_TYPE.NAVER_MAP:
    case SOURCE_TYPE.GANGNAMUNNI:
    case SOURCE_TYPE.SEPHORA:
    case SOURCE_TYPE.GOOGLE_MAP_REVIEW:
    case SOURCE_TYPE.THRIVE_MARKET:
    case SOURCE_TYPE.LAZADA:
    case SOURCE_TYPE.SHOPEE:
      return FEEDBACK_TYPE.REVIEW;

    case SOURCE_TYPE.SCRAPED_REDDIT:
    case SOURCE_TYPE.TWITTER:
    case SOURCE_TYPE.TIKTOK:
    case SOURCE_TYPE.NAVER_CAFE:
    case SOURCE_TYPE.NAVER_BLOG:
    case SOURCE_TYPE.FACEBOOK:
    case SOURCE_TYPE.INSTAGRAM:
    case SOURCE_TYPE.INSTAGRAM_REELS:
    case SOURCE_TYPE.DC_INSIDE:
    case SOURCE_TYPE.PPOMPPU:
    case SOURCE_TYPE.GOOGLE_NEWS:
    case SOURCE_TYPE.YOUTUBE:
      return FEEDBACK_TYPE.SOCIAL;

    case SOURCE_TYPE.KNOCOMMERCE:
    case SOURCE_TYPE.TYPEFORM:
      return FEEDBACK_TYPE.SURVEY;

    case SOURCE_TYPE.DISCORD:
      return FEEDBACK_TYPE.CHAT_APP;

    // case FeedbackSourceType.SOURCE_GITHUB:
    // case FeedbackSourceType.SOURCE_HUBSPOT:
    // case FeedbackSourceType.SOURCE_METABASE:

    case SOURCE_TYPE.SLACK_SALESFORCE:
      return null;
    default: {
      console.error(
        `Invalid source type: "${source}", it will be treated as ticket`,
      );
      return FEEDBACK_TYPE.TICKET;
    }
  }
};

const CommonShortFieldList: FEEDBACK_FIELD[] = [
  FEEDBACK_FIELD.KEY_SENTIMENT,
  FEEDBACK_FIELD.CLASSIFICATION,
  FEEDBACK_FIELD.TOPIC2_LIST,
  FEEDBACK_FIELD.LABELS,
  FEEDBACK_FIELD.DATE,
];

export const CommonFieldList: FEEDBACK_FIELD[] = [
  FEEDBACK_FIELD.CUSTOMER_INFO,
  FEEDBACK_FIELD.KEY_SENTIMENT,
  FEEDBACK_FIELD.ENDING_SENTIMENT,
  FEEDBACK_FIELD.SENTIMENT,
  FEEDBACK_FIELD.CLASSIFICATION,
  FEEDBACK_FIELD.TOPIC2_LIST,
  FEEDBACK_FIELD.LABELS,
  FEEDBACK_FIELD.PRODUCTS,
  FEEDBACK_FIELD.LANGUAGE,
  FEEDBACK_FIELD.SOURCE,
  FEEDBACK_FIELD.SOURCE_ID,
  FEEDBACK_FIELD.DATA_ORIGIN,
  FEEDBACK_FIELD.DATE,
  FEEDBACK_FIELD.STATUS,
];

export const CommonSubFieldList: FEEDBACK_FIELD[] = [
  FEEDBACK_FIELD.MANAGER,
  FEEDBACK_FIELD.REMARKS,
  FEEDBACK_FIELD.BOOKMARKS,
];

// Ticket
export const TicketShortFieldList: FEEDBACK_FIELD[] = [
  FEEDBACK_FIELD.CUSTOMER_INFO,
  ...CommonShortFieldList,
  FEEDBACK_FIELD.SCORES,
];
export const TicketFieldList: FEEDBACK_FIELD[] = [
  ...new Set([
    ...CommonFieldList,
    ...TicketShortFieldList,
    FEEDBACK_FIELD.PRIORITY,
    FEEDBACK_FIELD.SOURCE_STATUS,
    FEEDBACK_FIELD.SOURCE_ID,
    FEEDBACK_FIELD.DATA_ORIGIN,
    ...CommonSubFieldList,
  ]),
];

// Review
export const ReviewShortFieldList: FEEDBACK_FIELD[] = [
  FEEDBACK_FIELD.CUSTOMER_INFO,
  ...CommonShortFieldList,
  FEEDBACK_FIELD.PRODUCTS,
  FEEDBACK_FIELD.RATING_SCORE,
  FEEDBACK_FIELD.AD_PREDICTION,
  FEEDBACK_FIELD.IMPRESSION,
  FEEDBACK_FIELD.ENGAGEMENT,
  FEEDBACK_FIELD.ENGAGEMENT_RATE,
  FEEDBACK_FIELD.TOTAL_ENGAGEMENT,
];
export const ReviewFieldList: FEEDBACK_FIELD[] = [
  ...new Set([
    ...CommonFieldList,
    ...ReviewShortFieldList,
    FEEDBACK_FIELD.DEMOGRAPHIC,
    FEEDBACK_FIELD.LANGUAGE,
    FEEDBACK_FIELD.AD_PREDICTION,
    ...CommonSubFieldList,
  ]),
];

// Chat App
export const ChatAppShortFieldList: FEEDBACK_FIELD[] = [
  ...CommonShortFieldList,
];
export const ChatAppFieldList: FEEDBACK_FIELD[] = [
  ...new Set([
    ...CommonFieldList,
    ...ChatAppShortFieldList,
    ...CommonSubFieldList,
  ]),
];

// Social
export const SocialShortFieldList: FEEDBACK_FIELD[] = [
  ...CommonShortFieldList,
  FEEDBACK_FIELD.DEMOGRAPHIC,
  FEEDBACK_FIELD.AD_PREDICTION,
  FEEDBACK_FIELD.IMPRESSION,
  FEEDBACK_FIELD.ENGAGEMENT,
  FEEDBACK_FIELD.ENGAGEMENT_RATE,
  FEEDBACK_FIELD.TOTAL_ENGAGEMENT,
];
export const SocialFieldList: FEEDBACK_FIELD[] = [
  ...new Set([
    ...CommonFieldList,
    ...SocialShortFieldList,
    FEEDBACK_FIELD.LANGUAGE,
    FEEDBACK_FIELD.HASHTAGS,
    FEEDBACK_FIELD.MENTIONS,
    FEEDBACK_FIELD.AD_PREDICTION,
    ...CommonSubFieldList,
  ]),
];

// Survey

export const SurveyShortFieldList: FEEDBACK_FIELD[] = [
  FEEDBACK_FIELD.CUSTOMER_INFO,
  ...CommonShortFieldList,
  FEEDBACK_FIELD.SURVEY,
];
export const SurveyFieldList: FEEDBACK_FIELD[] = [
  ...new Set([
    ...CommonFieldList,
    ...SurveyShortFieldList,
    ...CommonSubFieldList,
  ]),
];

// Full
export const FullFieldList: FEEDBACK_FIELD[] = [
  ...new Set([
    ...CommonFieldList,
    ...SurveyShortFieldList,
    ...SocialShortFieldList,
    ...ChatAppShortFieldList,
    ...TicketShortFieldList,
    ...ReviewShortFieldList,
    ...CommonSubFieldList,
  ]),
];

export const FeedbackFieldListMap = new Map<FEEDBACK_TYPE, FEEDBACK_FIELD[]>([
  [FEEDBACK_TYPE.TICKET, TicketFieldList],
  [FEEDBACK_TYPE.REVIEW, ReviewFieldList],
  [FEEDBACK_TYPE.CHAT_APP, ChatAppFieldList],
  [FEEDBACK_TYPE.SOCIAL, SocialFieldList],
  [FEEDBACK_TYPE.SURVEY, SurveyFieldList],
]);

export const FeedbackFieldShortListMap = new Map<
  FEEDBACK_TYPE,
  FEEDBACK_FIELD[]
>([
  [FEEDBACK_TYPE.TICKET, TicketShortFieldList],
  [FEEDBACK_TYPE.REVIEW, ReviewShortFieldList],
  [FEEDBACK_TYPE.CHAT_APP, ChatAppShortFieldList],
  [FEEDBACK_TYPE.SOCIAL, SocialShortFieldList],
  [FEEDBACK_TYPE.SURVEY, SurveyShortFieldList],
]);

export const getFeedbackFieldListBySourceType = (
  source: SOURCE_TYPE,
  bodyType: FEEDBACK_BODY_TYPE,
  showFull: boolean,
) => {
  const feedbackType = getFeedbackType(source, bodyType);
  if (showFull) {
    if (!feedbackType) return FullFieldList;
    return FeedbackFieldListMap.get(feedbackType) || FullFieldList;
  } else {
    if (!feedbackType) return FullFieldList.slice(0, 5);
    return (
      FeedbackFieldShortListMap.get(feedbackType) || FullFieldList.slice(0, 5)
    );
  }
};
